import { APP_INITIALIZER, NgModule } from "@angular/core";
import { registerLocaleData } from "@angular/common";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import localeDe from '@angular/common/locales/de-CH';
import localeFr from '@angular/common/locales/fr-CH';
import localeIt from '@angular/common/locales/it-CH';
import { AppConfig } from "./app.config";
import { FormatConfigService } from "./core/services/format-config.service";
import { AuthConfig, OAuthModule, OAuthModuleConfig, OAuthService, OAuthStorage } from "angular-oauth2-oidc";
import { ForbiddenComponent } from './security/forbidden/forbidden.component';
import { UnsupportedComponent } from './security/unsupported/unsupported.component';
import { TranslateModule } from "@ngx-translate/core";
import { LoadingBarRouterModule } from "@ngx-loading-bar/router";
import { TooManyRequestsComponent } from "./security/too-many-requests/too-many-requests.component";
import {
  TooManyIcpGrundstueckRequestsComponent
} from './security/too-many-icp-grundstueck-requests/too-many-icp-grundstueck-requests.component';
import { MizenAngularLibConfig, MizenAngularLibModule } from "mizen-angular-lib";
import { LogoutComponent } from './security/logout/logout.component';

registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeIt);

@NgModule({
  imports: [
    AppRoutingModule.forRoot(),
    LoadingBarRouterModule,
    MizenAngularLibModule.forRoot(mizenAngularLibConfig()),
    OAuthModule,
    TranslateModule.forRoot(),
    LoadingBarRouterModule
  ],
  declarations: [
    AppComponent,
    ForbiddenComponent,
    UnsupportedComponent,
    TooManyRequestsComponent,
    TooManyIcpGrundstueckRequestsComponent,
    LogoutComponent,
  ],
  exports: [
    OAuthModule
  ],
  providers: [
    AppConfig,
    OAuthModule,
    {
      provide: APP_INITIALIZER,
      useFactory: (config: AppConfig) => () => config.load(),
      deps: [AppConfig, FormatConfigService, OAuthService],
      multi: true,
    },
    {
      provide: OAuthStorage,
      useFactory: prefixedOauthStorageFactory
    },
    {
      provide: AuthConfig,
      useFactory: lowerCaseAllowedUrlsFactory
    },

    {
      provide: OAuthModuleConfig,
      useFactory: lowerCaseAllowedUrlsFactory
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}

function lowerCaseAllowedUrlsFactory() {
  const environment = (window as any).environment;
  const urls = [];
  if (environment.apiUrl.toLowerCase()) {
    urls.push(environment.apiUrl.toLowerCase());
  }
  if (environment.mizenBasePath?.toLowerCase()) {
    urls.push(environment.mizenBasePath.toLowerCase());
  }

  return {
    resourceServer: {
      // allowedUrls need to be lowerCase because angular-oauth2-oidc only adds the Authorization Header if the
      // _lowercase_ urls match
      allowedUrls: urls,
      sendAccessToken: true,
    },
    ...environment.openIdConnect
  };
}

function mizenAngularLibConfig(): MizenAngularLibConfig {
  const environment = (window as any).environment;

  return {
    basePath: environment.mizenBasePath
  };
}

class PrefixedOAuthStorage implements OAuthStorage {

  prefix: string;

  constructor(private oAuthStorage: OAuthStorage) {
    this.prefix = this.createPrefix();
  }

  private createPrefix(): string {
    const app = (window as any).environment.deploymentMode === 'PUBLIC' ? 'icp' : 'capi';
    const kanton = (window as any).environment.kanton;
    return app + "_" + kanton + "_";
  }

  private getKey(key: string) {
    return this.prefix + key;
  }

  getItem(key: string): string | null {
    return this.oAuthStorage.getItem(this.getKey(key));
  }

  removeItem(key: string): void {
    this.oAuthStorage.removeItem(this.getKey(key));
  }

  setItem(key: string, data: string): void {
    this.oAuthStorage.setItem(this.getKey(key), data);
  }
}

function prefixedOauthStorageFactory(): OAuthStorage {
  return new PrefixedOAuthStorage(window.sessionStorage);
}
